import React from 'react';
import { Plus, X, MapPin, Info, RotateCcw, Loader2 } from 'lucide-react';

export default function DistributionBlock({
  transactionDistribution,
  updateDistributionRow,
  calculateDistributionTotal,
  handleAddRow,
  handleRemoveRow,
  usingStateOverride = false,
  stateOverrideInfo = null,
  onResetToDefault = null,
  isLoadingDistribution = false
}) {
  return (
    <div className="card-modern p-0 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        <div className="relative z-10 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">
            BLOCK 5: TRANSACTION DISTRIBUTION BY COLLECTION AMOUNT <span className="text-accent text-sm ml-2">(All fields required *)</span>
          </h2>
          <div className="flex items-center gap-3">
            {isLoadingDistribution && (
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/30">
                <Loader2 className="w-4 h-4 text-white animate-spin" />
                <span className="text-xs font-semibold text-white">Loading distribution...</span>
              </div>
            )}
            {usingStateOverride && stateOverrideInfo && (
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/30">
                <MapPin className="w-4 h-4 text-yellow-300" />
                <span className="text-xs font-semibold text-white">
                  Using {stateOverrideInfo.state}-specific distribution
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="p-6">
      {usingStateOverride && stateOverrideInfo && onResetToDefault && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-blue-900">
            <p className="font-semibold">State-Specific Distribution Active</p>
            <p className="text-blue-700 mt-1">
              This calculation is using a custom distribution configured for <strong>{stateOverrideInfo.state}</strong> + <strong>{stateOverrideInfo.service}</strong> instead of the default service distribution.
            </p>
          </div>
          <button
            onClick={onResetToDefault}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-lg text-xs font-semibold transition-colors"
            title="Reset to default service distribution"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Default
          </button>
        </div>
      )}
      <p className="text-sm text-slate-600 mb-4 italic">
        Sourcing: 1 - Target Historical Data, 2 - Existing Customer Acct Data, 3 - Working with GTM Team member
      </p>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left p-2">Collection Amount</th>
              <th className="text-left p-2">Low <span className="text-red-600">*</span></th>
              <th className="text-left p-2">Midpt <span className="text-red-600">*</span></th>
              <th className="text-left p-2">High <span className="text-red-600">*</span></th>
              <th className="text-left p-2">Current % <span className="text-red-600">*</span></th>
              <th className="text-left p-2">Notes</th>
              {handleRemoveRow && <th className="text-left p-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {transactionDistribution.map((row, index) => (
              <tr key={index} className="border-b">
                <td className="p-2 font-medium text-cyan-700">{row.collectionAmount}</td>
                <td className="p-2">
                  <input
                    type="number"
                    value={row.low}
                    onChange={(e) => updateDistributionRow(index, 'low', e.target.value)}
                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                    className="input-field text-sm w-28 bg-yellow-50"
                    step="0.01"
                  />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    value={row.mid}
                    readOnly
                    className="input-field text-sm w-28 bg-green-50 cursor-not-allowed"
                    title="Auto-calculated: (Low + High) / 2"
                  />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    value={row.high}
                    onChange={(e) => updateDistributionRow(index, 'high', e.target.value)}
                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                    className="input-field text-sm w-28 bg-yellow-50"
                    step="0.01"
                  />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    value={row.currentPercent}
                    onChange={(e) => updateDistributionRow(index, 'currentPercent', e.target.value)}
                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                    className="input-field text-sm w-24 bg-yellow-50"
                    step="0.01"
                    max="100"
                  />
                </td>
                <td className="p-2 text-xs text-slate-500">
                  {index === 0 ? 'Transactions at or below Tier 1' : 
                   index === 1 ? 'Transactions between Tier 1 and...' : ''}
                </td>
                {handleRemoveRow && (
                  <td className="p-2">
                    {transactionDistribution.length > 1 && (
                      <button
                        onClick={() => handleRemoveRow(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove row"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold">
              <td className="p-2" colSpan="4">Total</td>
              <td className="p-2">
                <span className={Math.abs(parseFloat(calculateDistributionTotal()) - 100) < 0.01 ? 'text-green-600' : 'text-red-600'}>
                  {calculateDistributionTotal()}%
                </span>
              </td>
              <td className="p-2 text-xs text-red-600 italic" colSpan={handleRemoveRow ? 2 : 1}>
                Should equal 100%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {handleAddRow && (
        <div className="mt-4">
          <button
            onClick={handleAddRow}
            className="flex items-center space-x-2 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-2 rounded"
          >
            <Plus className="w-4 h-4" />
            <span>Add Row</span>
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
